/**
 * CampaignRun frontend entity — Patch 34.2.1.
 *
 * Canonical types + query keys + React Query hooks + mutation-invalidation
 * helper for the CampaignRun projection (Patch 34.2.0 backend). Every
 * founder-facing surface that shows campaign counts, health, or stage
 * should read from here so nothing computes its own truth.
 */

import { useQuery, type QueryClient } from "@tanstack/react-query";
import { http } from "@/shared/api/http";

// ── Types ────────────────────────────────────────────────────────────

export type RunStage =
  | "blueprint" | "pack" | "generating" | "reconciling"
  | "review" | "approved" | "scheduled" | "published" | "archived";

export type RunHealth =
  | "healthy" | "generating" | "promotion_incomplete"
  | "reconciliation_failed" | "partial" | "failed" | "blocked";

export interface CampaignRunCounters {
  requested: number;
  generated: number;
  persisted: number;
  promoted: number;
  review_visible: number;
  draft: number;
  approved: number;
  scheduled: number;
  published: number;
  failed: number;
}

export interface CampaignRun {
  id: string;
  workspace_id: string;
  campaign_pack_id: string;
  blueprint_id: string | null;
  campaign_name: string;
  objective: string;
  stage: RunStage;
  health: RunHealth;
  blocker: string | null;
  counters: CampaignRunCounters;
  started_at: string | null;
  last_activity_at: string | null;
  completed_at: string | null;
}

export interface ProjectionIntegrity {
  packs_read: number;
  runs_returned: number;
  runs_skipped: number;
  incident_id: string | null;
}

export interface CampaignRunSummary {
  workspace_id: string;
  runs_total: number;
  runs_by_health: Partial<Record<RunHealth, number>>;
  counters: CampaignRunCounters;
  reconciliation_pending: number;
  /** Patch 34.2.2 · surfaced when any pack failed to project. */
  projection_integrity?: ProjectionIntegrity;
}

export interface CampaignHealthCheckItem {
  label: string;
  ok: boolean;
  detail: string | null;
}

export interface CampaignHealthReport {
  campaign_run_id: string;
  stage: RunStage;
  health: RunHealth;
  blocker: string | null;
  checks: CampaignHealthCheckItem[];
}

/** Patch 34.2.1d/e/f · Actual validated, reviewable ai_asset inventory. */
export interface ReviewInventory {
  draft: number;
  needs_review: number;
  reviewable_total: number;

  // Patch 34.2.1f · linkage classification.
  valid_linked_total: number;
  orphan_count: number;
  dangling_pack_count: number;
  cross_workspace_pack_count: number;
  valid_pack_count: number;

  /** @deprecated Patch 34.2.1e alias — reads `valid_linked_total`. */
  linked_reviewable_total: number;
  /** @deprecated Patch 34.2.1d alias — reads `valid_pack_count`. */
  pack_count: number;

  rows_read: number;
  rows_returned: number;
  rows_skipped: number;
  skip_reasons: Record<string, number>;
  incident_id: string | null;
}

// ── Query keys ───────────────────────────────────────────────────────

export const campaignRunKeys = {
  all: (workspaceId: string) => ["campaign-runs", workspaceId] as const,
  // Patch 34.2.1b · include ``limit`` so different pagination windows do
  // not collide on the same cache slot.
  list: (workspaceId: string, limit = 50) =>
    ["campaign-runs", workspaceId, "list", limit] as const,
  summary: (workspaceId: string) => ["campaign-runs", workspaceId, "summary"] as const,
  detail: (workspaceId: string, runId: string) =>
    ["campaign-runs", workspaceId, "detail", runId] as const,
  health: (workspaceId: string, runId: string) =>
    ["campaign-runs", workspaceId, "health", runId] as const,
  reviewInventory: (workspaceId: string) =>
    ["campaign-runs", workspaceId, "review-inventory"] as const,
  byBlueprint: (workspaceId: string, blueprintId: string) =>
    ["campaign-runs", workspaceId, "by-blueprint", blueprintId] as const,
};

// ── API calls ────────────────────────────────────────────────────────

export async function fetchCampaignRunSummary(
  workspaceId: string,
): Promise<CampaignRunSummary> {
  return http<CampaignRunSummary>(
    `/workspaces/${workspaceId}/campaign-runs/summary`,
  );
}

export async function fetchCampaignRuns(
  workspaceId: string,
  limit = 50,
): Promise<CampaignRun[]> {
  return http<CampaignRun[]>(
    `/workspaces/${workspaceId}/campaign-runs?limit=${limit}`,
  );
}

export async function fetchCampaignRun(
  workspaceId: string,
  runId: string,
): Promise<CampaignRun> {
  return http<CampaignRun>(`/workspaces/${workspaceId}/campaign-runs/${runId}`);
}

export async function fetchCampaignHealth(
  workspaceId: string,
  runId: string,
): Promise<CampaignHealthReport> {
  return http<CampaignHealthReport>(
    `/workspaces/${workspaceId}/campaign-runs/${runId}/health`,
  );
}

export async function fetchReviewInventory(
  workspaceId: string,
): Promise<ReviewInventory> {
  return http<ReviewInventory>(
    `/workspaces/${workspaceId}/campaign-runs/review-inventory`,
  );
}

/** Patch 34.2.1e · Targeted CampaignRun lookup by Blueprint. */
export async function fetchCampaignRunByBlueprint(
  workspaceId: string,
  blueprintId: string,
): Promise<CampaignRun> {
  return http<CampaignRun>(
    `/workspaces/${workspaceId}/campaign-runs/by-blueprint/${blueprintId}`,
  );
}

// ── React Query hooks ────────────────────────────────────────────────

export function useCampaignRunSummary(workspaceId: string | null) {
  return useQuery({
    queryKey: workspaceId
      ? campaignRunKeys.summary(workspaceId)
      : ["campaign-runs", "no-workspace", "summary"],
    queryFn: () => fetchCampaignRunSummary(workspaceId as string),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

export function useCampaignRuns(workspaceId: string | null, limit = 50) {
  return useQuery({
    queryKey: workspaceId
      ? campaignRunKeys.list(workspaceId, limit)
      : ["campaign-runs", "no-workspace", "list", limit],
    queryFn: () => fetchCampaignRuns(workspaceId as string, limit),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

export function useCampaignHealth(workspaceId: string | null, runId: string | null) {
  return useQuery({
    queryKey: workspaceId && runId
      ? campaignRunKeys.health(workspaceId, runId)
      : ["campaign-runs", "no-workspace", "health"],
    queryFn: () => fetchCampaignHealth(workspaceId as string, runId as string),
    enabled: !!workspaceId && !!runId,
    staleTime: 15_000,
  });
}

export function useReviewInventory(workspaceId: string | null) {
  return useQuery({
    queryKey: workspaceId
      ? campaignRunKeys.reviewInventory(workspaceId)
      : ["campaign-runs", "no-workspace", "review-inventory"],
    queryFn: () => fetchReviewInventory(workspaceId as string),
    enabled: !!workspaceId,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Patch 34.2.1e · Resolve blueprint → CampaignRun with ONE targeted
 * request. Replaces the previous `useCampaignRuns(workspaceId, 200)`
 * scan used by the Campaign Room health panel. Returns null when the
 * workspace has no run for the blueprint (404 handled as
 * ``notFound`` — React Query still marks the query as errored but
 * consumers can distinguish by inspecting `error.status`).
 */
export function useCampaignRunByBlueprint(
  workspaceId: string | null,
  blueprintId: string | null,
) {
  return useQuery({
    queryKey: workspaceId && blueprintId
      ? campaignRunKeys.byBlueprint(workspaceId, blueprintId)
      : ["campaign-runs", "no-workspace", "by-blueprint"],
    queryFn: () =>
      fetchCampaignRunByBlueprint(workspaceId as string, blueprintId as string),
    enabled: !!workspaceId && !!blueprintId,
    staleTime: 15_000,
    retry: (failureCount, error) => {
      // Don't retry on 404 — that's the "no run for this blueprint yet"
      // signal used by the unavailable-state branch.
      const status = (error as { status?: number } | null)?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

// ── Invalidation helper (Part VIII) ──────────────────────────────────
/**
 * Invalidate every query key that could shift after a lifecycle-mutating
 * action (approve / reject / schedule / unschedule / publish / archive /
 * regenerate). Call this from every mutation's onSuccess.
 *
 * Patch 34.2.1c · Keys below are verified against the actual source of
 * every consumer, NOT guessed. Any change here must be re-verified with
 * `grep queryKey: /app/frontend/src`.
 */
export function invalidateCampaignLifecycle(
  queryClient: QueryClient,
  workspaceId: string,
  runId?: string,
): void {
  // Canonical CampaignRun namespace for this workspace.
  queryClient.invalidateQueries({ queryKey: campaignRunKeys.all(workspaceId) });
  if (runId) {
    queryClient.invalidateQueries({
      queryKey: campaignRunKeys.detail(workspaceId, runId),
    });
    queryClient.invalidateQueries({
      queryKey: campaignRunKeys.health(workspaceId, runId),
    });
  }

  // ── Content Review queue ────────────────────────────────────────────
  // Verified in `pages/ops/ContentOpsPage.tsx`:
  //   • ["ops-summary", ws.id]     — summary counters + campaigns_awaiting_review.
  //   • ["ops-packs", ws.id]       — pack list on the Review page.
  //   • ["ops-pack-assets", …]     — assets inside the currently open pack.
  //   • ["ops-asset-history", …]   — history drawer.
  //   • ["ops-asset-versions", …]  — versions drawer.
  queryClient.invalidateQueries({ queryKey: ["ops-summary", workspaceId] });
  queryClient.invalidateQueries({ queryKey: ["ops-packs", workspaceId] });
  queryClient.invalidateQueries({ queryKey: ["ops-pack-assets"] });

  // ── Vault ───────────────────────────────────────────────────────────
  // Verified in `pages/vault-hub/VaultHubPage.tsx` (`["vault-assets", …]`)
  // and `entities/workspace/milestones.ts` (`["assets-rollup", …]`).
  queryClient.invalidateQueries({ queryKey: ["vault-assets"] });
  queryClient.invalidateQueries({ queryKey: ["assets-rollup"] });

  // ── Publishing ──────────────────────────────────────────────────────
  // Verified in `pages/publishing/PublishingPage.tsx` (`["publishing", ws]`),
  // `pages/publishing-history/PublishingHistoryPage.tsx`
  // (`["publishing", "history", …]`), `pages/channels/ChannelsPage.tsx`
  // (`["publishing", "channels", …]`), and the
  // `widgets/publishing-overview/PublishingOverviewCard.tsx`
  // (`["publishing", "scoreboard", …]`). React Query matches by prefix,
  // so a single `["publishing"]` invalidation covers them all.
  queryClient.invalidateQueries({ queryKey: ["publishing"] });

  // ── Publishing Calendar / Mission Control ───────────────────────────
  // Verified in `pages/mission-control/MissionControlPage.tsx` — every
  // mc-scoped query lives under the `["mc", …]` prefix.
  queryClient.invalidateQueries({ queryKey: ["mc"] });

  // ── Campaign packs (Marketing Strategy view) ────────────────────────
  // Verified in `features/marketing-strategy/ui/MarketingStrategyView.tsx`.
  queryClient.invalidateQueries({ queryKey: ["campaign-packs"] });

  // ── Campaigns list + Campaign Room ──────────────────────────────────
  // Patch 34.2.1d · verified in
  //   `pages/campaigns-hub/CampaignsHubPage.tsx` — `["campaigns", "hub", ws]`
  //   `features/campaign-room/ui/CampaignRoomView.tsx` — `["campaign-room", blueprintId]`
  // React Query invalidates by prefix, so both `campaigns` and
  // `campaign-room` roots are enough.
  queryClient.invalidateQueries({ queryKey: ["campaigns"] });
  queryClient.invalidateQueries({ queryKey: ["campaign-room"] });
}
